import React, { useState, useEffect } from 'react';
import { Info, Globe } from 'lucide-react';
import { convertScheduleToLocalTime, getUserTimeZoneDisplay } from '../common/timeZoneUtils';

const WeekCalendar = ({ schedule }) => {
    const [showTooltip, setShowTooltip] = useState(null);
    const [localSchedule, setLocalSchedule] = useState({});
    const [userTimeZone, setUserTimeZone] = useState('');

    const hours = Array.from({ length: 24 }, (_, i) => i);
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    useEffect(() => {
        if (schedule) {
            const convertedSchedule = convertScheduleToLocalTime(schedule);
            setLocalSchedule(convertedSchedule);
        }
        setUserTimeZone(getUserTimeZoneDisplay());
    }, [schedule]);

    const timeToMinutes = (time) => {
        const [hours, minutes] = time.split(':').map(Number);
        return hours * 60 + (minutes || 0);
    };

    const isTimeInRange = (hour, day) => {
        const scheduleForDay = localSchedule[day] || [];
        const currentMinutes = hour * 60;
        const nextHourMinutes = (hour + 1) * 60;

        return scheduleForDay.some(slot => {
            const startMinutes = timeToMinutes(slot.start);
            const endMinutes = timeToMinutes(slot.end);
            return (currentMinutes < endMinutes) && (nextHourMinutes > startMinutes);
        });
    };

    const findSlotsForHour = (hour, day) => {
        const scheduleForDay = localSchedule[day] || [];
        const currentMinutes = hour * 60;

        return scheduleForDay.filter(slot => {
            const startMinutes = timeToMinutes(slot.start);
            const endMinutes = timeToMinutes(slot.end);
            return (currentMinutes >= startMinutes) && (currentMinutes < endMinutes);
        });
    };

    const formatHour = (hour) => {
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${displayHour}${period}`;
    };

    const generateTooltipId = (day, hour, slotIndex) => {
        return `${day}-${hour}-${slotIndex}`;
    };

    return (
        <div className="overflow-auto">
            <div className="flex items-center justify-end mb-2">
                <Globe className="h-4 w-4 mr-1 text-gray-500" />
                <span className="text-sm text-gray-600">
                    Times shown in {userTimeZone}
                </span>
            </div>

            <div className="min-w-max grid grid-cols-[6rem_repeat(7,_minmax(120px,_1fr))] gap-[1px] bg-gray-200">
                {/* Header */}
                <div className="bg-gray-50 p-2 sticky left-0 z-10"></div>
                {days.map(day => (
                    <div key={day} className="bg-gray-50 p-2 text-center font-medium capitalize">
                        {day}
                    </div>
                ))}

                {/* Time slots */}
                {hours.map(hour => (
                    <React.Fragment key={hour}>
                        <div className="bg-white p-2 text-sm text-gray-500 text-right sticky left-0 z-10">
                            {formatHour(hour)}
                        </div>
                        {days.map(day => {
                            const isActive = isTimeInRange(hour, day);
                            const slots = findSlotsForHour(hour, day);

                            return (
                                <div
                                    key={`${day}-${hour}`}
                                    className={`p-2 border-t border-gray-100 transition-colors duration-200 relative ${
                                        isActive
                                            ? 'bg-green-300 hover:bg-green-400'
                                            : 'bg-red-300 hover:bg-red-400'
                                    }`}
                                >
                                    <div className="w-full h-full">
                                        {/* If we have multiple slots for this hour, show a visual indicator */}
                                        {slots.length > 1 && (
                                            <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs">
                                                {slots.length}
                                            </div>
                                        )}
                                    </div>

                                    {/* Show tooltips for each slot */}
                                    {slots.map((slot, index) => (
                                        <div
                                            key={index}
                                            className="absolute inset-0 cursor-pointer"
                                            onMouseEnter={() => setShowTooltip(generateTooltipId(day, hour, index))}
                                            onMouseLeave={() => setShowTooltip(null)}
                                        >
                                            {showTooltip === generateTooltipId(day, hour, index) && (
                                                <div className="absolute z-20 bg-gray-800 text-white text-xs rounded py-2 px-3 left-1/2 -translate-x-1/2 bottom-full mb-1 shadow-lg whitespace-nowrap">
                                                    <div className="font-bold">{slot.start} - {slot.end}</div>
                                                    {slot.label && <div>{slot.label}</div>}
                                                    {slot.isContinuation && (
                                                        <div className="text-xs text-gray-300">Continued from previous day</div>
                                                    )}
                                                    {slot.spansDays && (
                                                        <div className="text-xs text-gray-300">Continues to next day</div>
                                                    )}
                                                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-gray-800"></div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </React.Fragment>
                ))}
            </div>

            {/* Legend and info */}
            <div className="mt-4 flex items-start justify-between text-sm">
                <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                        <div className="w-3 h-3 bg-green-300 mr-2"></div>
                        <span className="text-gray-600">Active Hours</span>
                    </div>
                    <div className="flex items-center">
                        <div className="w-3 h-3 bg-red-300 mr-2"></div>
                        <span className="text-gray-600">Inactive Hours</span>
                    </div>
                    <div className="flex items-center">
                        <div className="w-3 h-3 bg-blue-500 mr-2 rounded-full"></div>
                        <span className="text-gray-600">Multiple Time Slots</span>
                    </div>
                </div>

                <div className="flex items-start text-gray-500">
                    <Info className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" />
                    <span>Hover over active hours to see scheduled time slot details. Multiple active slots per day are supported.</span>
                </div>
            </div>
        </div>
    );
};

export default WeekCalendar;